import {
	MARKER_POPUP_TIP,
	MarkerPopupArrow,
	type MarkerPopupArrow as MarkerPopupArrowValue,
} from "@/components/Map/MarkerPopup";

/** Matches MarkerPopup card `w-[300px]`. */
export const MAP_POPUP_ESTIMATED_WIDTH_PX = 300;
/** Typical MarkerPopup height with title + badge + CTA. */
export const MAP_POPUP_ESTIMATED_HEIGHT_PX = 200;
const MAP_POPUP_EDGE_PADDING_PX = 8;
/**
 * Space between caret tip and map anchor. Keep near 0 so the tip stays on
 * the geometry when placement flips.
 */
const MAP_POPUP_GAP_PX = 0;

/**
 * Corner tip inset from MarkerPopup / Figma Tooltip.
 * Tailwind classes below hardcode `14px` — keep in sync with this value.
 */
const CORNER_TIP_PX = MARKER_POPUP_TIP.cornerInsetPx;
if (import.meta.env.DEV && CORNER_TIP_PX !== 14) {
	throw new Error(
		`POPUP_PLACEMENT_WRAPPER_CLASS uses 14px literals; MARKER_POPUP_TIP.cornerInsetPx is ${CORNER_TIP_PX}`,
	);
}

export interface ResolvePopupPlacementArgs {
	anchorX: number;
	anchorY: number;
	containerWidth: number;
	containerHeight: number;
	popupWidth?: number;
	popupHeight?: number;
	padding?: number;
	gap?: number;
}

export interface PopupPlacement {
	arrow: MarkerPopupArrowValue;
	wrapperClassName: string;
}

interface PlacementCandidate {
	arrow: MarkerPopupArrowValue;
	wrapperClassName: string;
	/** Bounding box of the popup card in container coordinates. */
	left: number;
	top: number;
	right: number;
	bottom: number;
}

/**
 * Tip-centric wrappers on a 0×0 marker anchor (`left-0 top-0` + translate).
 * Corner px values must be static Tailwind literals matching
 * {@link MARKER_POPUP_TIP.cornerInsetPx} (14) so JIT can see them.
 */
export const POPUP_PLACEMENT_WRAPPER_CLASS: Record<
	MarkerPopupArrowValue,
	string
> = {
	[MarkerPopupArrow.BottomMiddle]:
		"left-0 top-0 -translate-x-1/2 -translate-y-full",
	[MarkerPopupArrow.BottomLeft]:
		"left-0 top-0 -translate-x-[14px] -translate-y-full",
	[MarkerPopupArrow.BottomRight]:
		"left-0 top-0 -translate-y-full translate-x-[calc(-100%+14px)]",
	[MarkerPopupArrow.Top]: "left-0 top-0 -translate-x-1/2",
	[MarkerPopupArrow.TopMiddle]: "left-0 top-0 -translate-x-1/2",
	[MarkerPopupArrow.TopLeft]: "left-0 top-0 -translate-x-[14px]",
	[MarkerPopupArrow.TopRight]: "left-0 top-0 translate-x-[calc(-100%+14px)]",
	[MarkerPopupArrow.Left]: "left-0 top-0 -translate-y-1/2",
	[MarkerPopupArrow.LeftTop]: "left-0 top-0 -translate-y-[14px]",
	[MarkerPopupArrow.LeftBottom]: "left-0 top-0 translate-y-[calc(-100%+14px)]",
	[MarkerPopupArrow.Right]: "left-0 top-0 -translate-x-full -translate-y-1/2",
	[MarkerPopupArrow.RightTop]:
		"left-0 top-0 -translate-x-full -translate-y-[14px]",
	[MarkerPopupArrow.RightBottom]:
		"left-0 top-0 -translate-x-full translate-y-[calc(-100%+14px)]",
};

function overflowOutside(
	box: { left: number; top: number; right: number; bottom: number },
	containerWidth: number,
	containerHeight: number,
	padding: number,
): number {
	const leftOverflow = Math.max(0, padding - box.left);
	const topOverflow = Math.max(0, padding - box.top);
	const rightOverflow = Math.max(0, box.right - (containerWidth - padding));
	const bottomOverflow = Math.max(0, box.bottom - (containerHeight - padding));
	return leftOverflow + topOverflow + rightOverflow + bottomOverflow;
}

function candidatesForAnchor(
	anchorX: number,
	anchorY: number,
	popupWidth: number,
	popupHeight: number,
	gap: number,
	tipInset: number,
): PlacementCandidate[] {
	const halfW = popupWidth / 2;
	const halfH = popupHeight / 2;

	const above = {
		top: anchorY - gap - popupHeight,
		bottom: anchorY - gap,
	};
	const below = {
		top: anchorY + gap,
		bottom: anchorY + gap + popupHeight,
	};
	const toRight = {
		left: anchorX + gap,
		right: anchorX + gap + popupWidth,
	};
	const toLeft = {
		left: anchorX - gap - popupWidth,
		right: anchorX - gap,
	};

	return [
		{
			arrow: MarkerPopupArrow.BottomMiddle,
			wrapperClassName:
				POPUP_PLACEMENT_WRAPPER_CLASS[MarkerPopupArrow.BottomMiddle],
			left: anchorX - halfW,
			right: anchorX + halfW,
			...above,
		},
		{
			arrow: MarkerPopupArrow.BottomLeft,
			wrapperClassName:
				POPUP_PLACEMENT_WRAPPER_CLASS[MarkerPopupArrow.BottomLeft],
			left: anchorX - tipInset,
			right: anchorX - tipInset + popupWidth,
			...above,
		},
		{
			arrow: MarkerPopupArrow.BottomRight,
			wrapperClassName:
				POPUP_PLACEMENT_WRAPPER_CLASS[MarkerPopupArrow.BottomRight],
			left: anchorX + tipInset - popupWidth,
			right: anchorX + tipInset,
			...above,
		},
		{
			arrow: MarkerPopupArrow.TopMiddle,
			wrapperClassName:
				POPUP_PLACEMENT_WRAPPER_CLASS[MarkerPopupArrow.TopMiddle],
			left: anchorX - halfW,
			right: anchorX + halfW,
			...below,
		},
		{
			arrow: MarkerPopupArrow.TopLeft,
			wrapperClassName: POPUP_PLACEMENT_WRAPPER_CLASS[MarkerPopupArrow.TopLeft],
			left: anchorX - tipInset,
			right: anchorX - tipInset + popupWidth,
			...below,
		},
		{
			arrow: MarkerPopupArrow.TopRight,
			wrapperClassName:
				POPUP_PLACEMENT_WRAPPER_CLASS[MarkerPopupArrow.TopRight],
			left: anchorX + tipInset - popupWidth,
			right: anchorX + tipInset,
			...below,
		},
		{
			arrow: MarkerPopupArrow.Left,
			wrapperClassName: POPUP_PLACEMENT_WRAPPER_CLASS[MarkerPopupArrow.Left],
			top: anchorY - halfH,
			bottom: anchorY + halfH,
			...toRight,
		},
		{
			arrow: MarkerPopupArrow.LeftTop,
			wrapperClassName: POPUP_PLACEMENT_WRAPPER_CLASS[MarkerPopupArrow.LeftTop],
			top: anchorY - tipInset,
			bottom: anchorY - tipInset + popupHeight,
			...toRight,
		},
		{
			arrow: MarkerPopupArrow.LeftBottom,
			wrapperClassName:
				POPUP_PLACEMENT_WRAPPER_CLASS[MarkerPopupArrow.LeftBottom],
			top: anchorY + tipInset - popupHeight,
			bottom: anchorY + tipInset,
			...toRight,
		},
		{
			arrow: MarkerPopupArrow.Right,
			wrapperClassName: POPUP_PLACEMENT_WRAPPER_CLASS[MarkerPopupArrow.Right],
			top: anchorY - halfH,
			bottom: anchorY + halfH,
			...toLeft,
		},
		{
			arrow: MarkerPopupArrow.RightTop,
			wrapperClassName:
				POPUP_PLACEMENT_WRAPPER_CLASS[MarkerPopupArrow.RightTop],
			top: anchorY - tipInset,
			bottom: anchorY - tipInset + popupHeight,
			...toLeft,
		},
		{
			arrow: MarkerPopupArrow.RightBottom,
			wrapperClassName:
				POPUP_PLACEMENT_WRAPPER_CLASS[MarkerPopupArrow.RightBottom],
			top: anchorY + tipInset - popupHeight,
			bottom: anchorY + tipInset,
			...toLeft,
		},
	];
}

function isLeftArrow(arrow: MarkerPopupArrowValue): boolean {
	return (
		arrow === MarkerPopupArrow.Left ||
		arrow === MarkerPopupArrow.LeftTop ||
		arrow === MarkerPopupArrow.LeftBottom
	);
}

function isRightArrow(arrow: MarkerPopupArrowValue): boolean {
	return (
		arrow === MarkerPopupArrow.Right ||
		arrow === MarkerPopupArrow.RightTop ||
		arrow === MarkerPopupArrow.RightBottom
	);
}

function isTopArrow(arrow: MarkerPopupArrowValue): boolean {
	return (
		arrow === MarkerPopupArrow.Top ||
		arrow === MarkerPopupArrow.TopMiddle ||
		arrow === MarkerPopupArrow.TopLeft ||
		arrow === MarkerPopupArrow.TopRight
	);
}

function isBottomArrow(arrow: MarkerPopupArrowValue): boolean {
	return (
		arrow === MarkerPopupArrow.BottomMiddle ||
		arrow === MarkerPopupArrow.BottomLeft ||
		arrow === MarkerPopupArrow.BottomRight
	);
}

/**
 * Tie-break rank when overflow is equal (lower wins).
 *
 * Without this, BottomLeft/TopLeft always beat Left* because they appear
 * earlier in the candidate list — Left/Right only won within ~20px of the
 * map edge. Prefer side placements in a wider side zone so Left/Right/
 * LeftTop/… actually appear while panning.
 */
function preferenceRank(
	arrow: MarkerPopupArrowValue,
	anchorX: number,
	anchorY: number,
	containerWidth: number,
	containerHeight: number,
	popupWidth: number,
	popupHeight: number,
	padding: number,
): number {
	const halfW = popupWidth / 2;
	/** Prefer Left/Right when BottomMiddle would clip horizontally. */
	const sideZone = halfW + padding;
	/**
	 * Only the outer vertical band uses LeftTop/LeftBottom (etc.). Keep this
	 * smaller than popupHeight so mid-edge anchors get Left/Right middle.
	 */
	const cornerZoneY = Math.min(100, popupHeight / 2) + padding;

	const nearLeft = anchorX <= sideZone;
	const nearRight = anchorX >= containerWidth - sideZone;
	const nearTop = anchorY <= cornerZoneY;
	const nearBottom = anchorY >= containerHeight - cornerZoneY;

	// Left edge — prefer opening to the right (Left* carets).
	if (nearLeft && !nearRight) {
		if (nearTop && !nearBottom) {
			if (arrow === MarkerPopupArrow.LeftTop) return 0;
			if (arrow === MarkerPopupArrow.TopLeft) return 1;
			if (isLeftArrow(arrow)) return 2;
			if (isTopArrow(arrow)) return 3;
			return 4;
		}
		if (nearBottom && !nearTop) {
			if (arrow === MarkerPopupArrow.LeftBottom) return 0;
			if (arrow === MarkerPopupArrow.BottomLeft) return 1;
			if (isLeftArrow(arrow)) return 2;
			if (isBottomArrow(arrow)) return 3;
			return 4;
		}
		if (arrow === MarkerPopupArrow.Left) return 0;
		if (isLeftArrow(arrow)) return 1;
		if (isBottomArrow(arrow)) return 2;
		if (isTopArrow(arrow)) return 3;
		return 4;
	}

	// Right edge — prefer opening to the left (Right* carets).
	if (nearRight && !nearLeft) {
		if (nearTop && !nearBottom) {
			if (arrow === MarkerPopupArrow.RightTop) return 0;
			if (arrow === MarkerPopupArrow.TopRight) return 1;
			if (isRightArrow(arrow)) return 2;
			if (isTopArrow(arrow)) return 3;
			return 4;
		}
		if (nearBottom && !nearTop) {
			if (arrow === MarkerPopupArrow.RightBottom) return 0;
			if (arrow === MarkerPopupArrow.BottomRight) return 1;
			if (isRightArrow(arrow)) return 2;
			if (isBottomArrow(arrow)) return 3;
			return 4;
		}
		if (arrow === MarkerPopupArrow.Right) return 0;
		if (isRightArrow(arrow)) return 1;
		if (isBottomArrow(arrow)) return 2;
		if (isTopArrow(arrow)) return 3;
		return 4;
	}

	// Default: prefer above, then below, then side.
	if (arrow === MarkerPopupArrow.BottomMiddle) return 0;
	if (isBottomArrow(arrow)) return 1;
	if (arrow === MarkerPopupArrow.TopMiddle || arrow === MarkerPopupArrow.Top)
		return 2;
	if (isTopArrow(arrow)) return 3;
	if (isLeftArrow(arrow) || isRightArrow(arrow)) return 4;
	return 5;
}

/**
 * Picks a {@link MarkerPopupArrow} + wrapper classes so the popup stays inside
 * the map container as much as possible, with the caret tip on the anchor.
 *
 * Minimizes clipped overflow first. On ties, prefers side placements near the
 * left/right edges (so Left/Right/LeftTop/… show up), otherwise BottomMiddle.
 */
export function resolvePopupPlacement(
	args: ResolvePopupPlacementArgs,
): PopupPlacement {
	const popupWidth = args.popupWidth ?? MAP_POPUP_ESTIMATED_WIDTH_PX;
	const popupHeight = args.popupHeight ?? MAP_POPUP_ESTIMATED_HEIGHT_PX;
	const padding = args.padding ?? MAP_POPUP_EDGE_PADDING_PX;
	const gap = args.gap ?? MAP_POPUP_GAP_PX;

	const candidates = candidatesForAnchor(
		args.anchorX,
		args.anchorY,
		popupWidth,
		popupHeight,
		gap,
		CORNER_TIP_PX,
	);

	const first = candidates[0];
	if (first == null) {
		return {
			arrow: MarkerPopupArrow.BottomMiddle,
			wrapperClassName:
				POPUP_PLACEMENT_WRAPPER_CLASS[MarkerPopupArrow.BottomMiddle],
		};
	}

	let best = first;
	let bestOverflow = overflowOutside(
		best,
		args.containerWidth,
		args.containerHeight,
		padding,
	);
	let bestRank = preferenceRank(
		best.arrow,
		args.anchorX,
		args.anchorY,
		args.containerWidth,
		args.containerHeight,
		popupWidth,
		popupHeight,
		padding,
	);

	for (let index = 1; index < candidates.length; index += 1) {
		const candidate = candidates[index];
		if (candidate == null) continue;
		const overflow = overflowOutside(
			candidate,
			args.containerWidth,
			args.containerHeight,
			padding,
		);
		const rank = preferenceRank(
			candidate.arrow,
			args.anchorX,
			args.anchorY,
			args.containerWidth,
			args.containerHeight,
			popupWidth,
			popupHeight,
			padding,
		);
		if (
			overflow < bestOverflow ||
			(overflow === bestOverflow && rank < bestRank)
		) {
			best = candidate;
			bestOverflow = overflow;
			bestRank = rank;
		}
	}

	return {
		arrow: best.arrow,
		wrapperClassName: best.wrapperClassName,
	};
}
